import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useAuth } from '../contexts/AuthContext';
import { taskAPI, usersTableAPI, clockAPI, digestAPI, aiStandupAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { TaskStatusIndicator } from '../components/TaskStatusIndicator';
import {
  BarChart3, Users, Clock, CheckCircle2, AlertTriangle, Download, Timer, TrendingUp,
  ArrowLeft, ChevronRight, MapPin, FileText, Coffee, Eye, Mail, RefreshCw, Sparkles
} from 'lucide-react';
import { formatTaskDate } from '../lib/dateFormat';
import { TaskDetailSheet } from '../components/TaskDetailSheet';
import { roleLabel } from '../components/UserChip';
import WeeklyStandupPage from './WeeklyStandupPage';

const VALID_TABS = ['daily', 'weekly', 'monthly', 'standup'];

export default function TeamReportPage() {
  const { currentWorkspace } = useWorkspace();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // 2026-07 — Owner-only page per user directive. Any non-owner hitting the
  // URL directly is bounced with an "Insufficient permissions" message.
  // (Previously only team/member were blocked; now admins/managers too.)
  useEffect(() => {
    if (user && !(user.role === 'owner' || user.role === 'super_admin')) {
      toast.error('Insufficient permissions');
      navigate((user.role === 'team' || user.role === 'member') ? '/eod' : '/dashboard', { replace: true });
    }
  }, [user, navigate]);
  const initialTab = (() => {
    const t = new URLSearchParams(location.search).get('tab');
    return VALID_TABS.includes(t) ? t : 'weekly';
  })();
  const [members, setMembers] = useState([]);
  const [allTasks, setAllTasks] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(initialTab);
  const [selectedMember, setSelectedMember] = useState(null);
  // 2026-02-22 — Deep-link from email digest: ?member=<uid>&date=<yyyy-mm-dd>
  // auto-selects the member as soon as the workspace member list loads.
  // Also handles ?tab=digest-log to jump straight to the owner's
  // Digest Log section from notification deep-links.
  useEffect(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      const tab = sp.get('tab');
      if (tab === 'digest-log' && (user?.role === 'owner' || user?.role === 'super_admin')) {
        setPeriod('digest-log');
      }
    } catch { /* SSR / no window — ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);
  useEffect(() => {
    if (!members || !members.length) return;
    try {
      const sp = new URLSearchParams(window.location.search);
      const memberUid = sp.get('member');
      if (memberUid && !selectedMember) {
        const m = members.find(x => x.user_id === memberUid);
        if (m) setSelectedMember(m);
      }
    } catch { /* SSR / no window — ignore */ }
  }, [members, selectedMember]);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const todayStr = new Date().toISOString().slice(0, 10);

  // Sync ?tab= query param when period changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('tab') !== period) {
      params.set('tab', period);
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    }
  }, [period]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!currentWorkspace?.id) return;
    if (period === 'standup') return; // standup tab fetches its own data
    setLoading(true);
    Promise.all([
      // D56 PART A — Use EOD-scoped member list (filters by org chart via manager_id).
      // Owner sees everyone; Admin/Manager see own subtree; Team gets 403 (sidebar entry
      // already hidden — defense-in-depth only).
      usersTableAPI.listEodVisible(currentWorkspace.id),
      taskAPI.list(currentWorkspace.id, {}),
      taskAPI.timeEntries(currentWorkspace.id, null, period === 'daily' ? 1 : period === 'weekly' ? 7 : 30),
    ]).then(([mRes, tRes, teRes]) => {
      // 2026-02-22 — Sort team members alphabetically A-Z by first name
      // (case-insensitive). Falls back to email if name is empty.
      const sorted = [...(mRes.data || [])].sort((a, b) => {
        const an = ((a.name || a.email || '').trim().toLowerCase());
        const bn = ((b.name || b.email || '').trim().toLowerCase());
        return an.localeCompare(bn);
      });
      setMembers(sorted); setAllTasks(tRes.data); setTimeEntries(teRes.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, [currentWorkspace?.id, period]);

  const periodStart = period === 'daily' ? todayStr : period === 'weekly'
    ? new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10)
    : new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);

  const getMemberStats = (uid) => {
    const myTasks = allTasks.filter(t => (t.assigned_to || []).includes(uid));
    const completed = myTasks.filter(t => t.status === 'completed');
    const pending = myTasks.filter(t => t.status !== 'completed' && t.status !== 'cancelled');
    const overdue = myTasks.filter(t => t.due_date && t.due_date < todayStr && t.status !== 'completed');
    const completedInPeriod = completed.filter(t => t.updated_at >= periodStart);
    const myTime = timeEntries.filter(e => e.user_id === uid);
    const hours = Math.round(myTime.reduce((s, e) => s + (e.hours || 0), 0) * 10) / 10;
    const rate = myTasks.length > 0 ? Math.round((completed.length / myTasks.length) * 100) : 0;
    return { total: myTasks.length, completed: completed.length, pending: pending.length, overdue: overdue.length, completedInPeriod: completedInPeriod.length, hours, rate, tasks: myTasks };
  };

  const exportCSV = () => {
    const header = 'Name,Role,Total,Completed,Pending,Overdue,Rate,Hours\n';
    const rows = members.map(m => { const s = getMemberStats(m.user_id); return `${m.name},${m.role},${s.total},${s.completed},${s.pending},${s.overdue},${s.rate}%,${s.hours}h`; }).join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `team-report-${todayStr}.csv`; a.click();
  };

  if (loading && period !== 'standup') return <div className="max-w-5xl mx-auto p-8 space-y-4">{[1,2,3].map(i => <div key={i} className="h-16 bg-secondary/20 rounded-xl animate-pulse" />)}</div>;

  // Individual member view
  if (selectedMember) {
    return (
      <div className="max-w-5xl mx-auto p-4 md:p-8 space-y-6" data-testid="member-report">
        <button onClick={() => setSelectedMember(null)} className="flex items-center gap-1.5 text-[12px] text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-3 w-3" /> All Members
        </button>

        <div className="flex items-center gap-4">
          <div className="h-12 w-12 rounded-full bg-secondary flex items-center justify-center"><span className="text-lg font-semibold">{(selectedMember.name || '?')[0].toUpperCase()}</span></div>
          <div>
            <h1 className="text-xl font-display font-bold">{selectedMember.name}</h1>
            <div className="flex items-center gap-2 text-[12px] text-muted-foreground"><Badge variant="outline" className="text-[9px] capitalize">{selectedMember.role}</Badge><span>{selectedMember.email}</span></div>
          </div>
        </div>

        {/* 2026-02-23 USER DIRECTIVE — Owner view rebuilt.
            Removed: EOD completion rate, task metrics panel, task
            completion rate bar, overdue/in-progress sections, AI
            Standup. The member detail view is now ONLY the chronological
            EOD work logs in the new "Work Report:" format. */}
        <MemberEodReports
          workspaceId={currentWorkspace?.id}
          memberId={selectedMember.user_id}
          memberName={selectedMember.name}
        />

        <TaskDetailSheet taskId={selectedTaskId} open={!!selectedTaskId} onOpenChange={o => { if (!o) setSelectedTaskId(null); }} />
      </div>
    );
  }

  // Team list view
  const teamTotal = allTasks.length;
  const teamCompleted = allTasks.filter(t => t.status === 'completed').length;
  const teamRate = teamTotal > 0 ? Math.round((teamCompleted / teamTotal) * 100) : 0;

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-8 space-y-6" data-testid="team-report-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-display font-bold tracking-tight">Team Report</h1>
          <p className="text-[12px] text-muted-foreground mt-0.5">{members.length} members · {teamTotal} tasks · {teamRate}% completion</p>
        </div>
        <div className="flex items-center gap-2">
          <Tabs value={period} onValueChange={setPeriod}>
            <TabsList className="h-8">
              <TabsTrigger value="daily" className="text-[11px] h-6 px-2.5" data-testid="report-tab-daily">Today</TabsTrigger>
              <TabsTrigger value="weekly" className="text-[11px] h-6 px-2.5" data-testid="report-tab-weekly">Week</TabsTrigger>
              <TabsTrigger value="monthly" className="text-[11px] h-6 px-2.5" data-testid="report-tab-monthly">Month</TabsTrigger>
              <TabsTrigger value="standup" className="text-[11px] h-6 px-2.5" data-testid="report-tab-standup">Standup</TabsTrigger>
              {(user?.role === 'owner' || user?.role === 'super_admin') && (
                <TabsTrigger value="digest-log" className="text-[11px] h-6 px-2.5" data-testid="report-tab-digest-log">Digest Log</TabsTrigger>
              )}
            </TabsList>
          </Tabs>
          {period !== 'standup' && period !== 'digest-log' && (
            <Button variant="outline" size="sm" className="h-8 text-[11px]" onClick={exportCSV}><Download className="h-3 w-3 mr-1.5" /> CSV</Button>
          )}
        </div>
      </div>

      {period === 'standup' ? (
        <WeeklyStandupPage embedded />
      ) : period === 'digest-log' ? (
        <DigestLogSection workspaceId={currentWorkspace?.id} />
      ) : (
        <div className="space-y-1.5">
          {members.map(m => {
            const s = getMemberStats(m.user_id);
            return (
              <button key={m.user_id} onClick={() => setSelectedMember(m)} className="flex items-center gap-4 w-full p-4 rounded-lg card-hover-gold bg-card text-left group" data-testid={`report-${m.user_id}`}>
                <div className="h-10 w-10 rounded-full bg-secondary flex items-center justify-center shrink-0"><span className="text-[14px] font-semibold">{(m.name || '?')[0].toUpperCase()}</span></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2"><span className="text-[13px] font-semibold">{m.name}</span><Badge variant="outline" className="text-[9px] capitalize">{m.role}</Badge></div>
                  <div className="flex items-center gap-3 mt-1 text-[11px] text-muted-foreground">
                    <span>{s.total} tasks</span><span>{s.completed} done</span>{s.overdue > 0 && <span className="text-red-500/60">{s.overdue} overdue</span>}<span>{s.hours}h</span>
                    <span className="flex items-center gap-1"><TrendingUp className="h-3 w-3" />{s.completedInPeriod} {period === 'daily' ? 'today' : period === 'weekly' ? 'this week' : 'this month'}</span>
                  </div>
                </div>
                <div className="text-right shrink-0 w-16"><p className="text-[18px] font-display font-bold">{s.rate}%</p><Progress value={s.rate} className="h-1 mt-1" /></div>
                <ChevronRight className="h-4 w-4 text-muted-foreground/20 opacity-0 group-hover:opacity-100 shrink-0" />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// D68 Bug C1 — EOD-sourced stats widget for a member's profile.
// Replaces the legacy task-count widget which showed 0s for any member
// who happened to have no assigned tasks (even when they had real EOD
// submissions with logged hours). Sources data from /api/clock/eod-reports.
//   Total        = EOD reports in date range
//   Done         = reports with status=submitted (default)
//   Pending      = reports with status=draft
//   Overdue      = workdays without an EOD report past 19:00 UTC cutoff
//                  (only counts past dates, not today before cutoff)
//   Hours        = SUM of hours_worked across reports in range
//   Completion%  = Done / expected_workdays_in_range
// ──────────────────────────────────────────────────────────────────────
// ──────────────────────────────────────────────────────────────────────
// 2026-02-23 — REMOVED MemberEodStats (text 'EOD completion rate') and
// AiStandupCard per user directive. Definitions deleted to keep this
// file lean. Kept TaskRow because it's still used by the legacy
// task-detail flow elsewhere on this page.
// ──────────────────────────────────────────────────────────────────────

function TaskRow({ task, todayStr, onClick }) {
  const isComplete = task.status === 'completed' || task.status === 'cancelled';
  const isOverdue = task.due_date && task.due_date < todayStr && !isComplete;
  return (
    <button onClick={onClick} className="flex items-center gap-3 w-full py-2 px-3 rounded-md task-row-hover text-left">
      {!isComplete && <span className={`h-2 w-2 rounded-full shrink-0 ${isOverdue ? 'bg-red-500/60' : 'bg-foreground/20'}`} />}
      <span className="text-[12px] truncate flex-1">{task.title}</span>
      <TaskStatusIndicator status={task.status} size="xs" />
      {task.due_date && <span className={`text-[10px] ${isOverdue ? 'text-red-500/60' : 'text-muted-foreground/40'}`}>{formatTaskDate(task.due_date)}</span>}
    </button>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Iter 63 5B+5C — EOD detail panel for a member.
// Lists today's clock-in/out + breaks + per-task timer cycles + other_work
// + total-vs-task-hours gap. Each entry has an "Export PDF" button.
// ──────────────────────────────────────────────────────────────────────
function EodDetailSection({ workspaceId, memberId, onTaskClick }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!workspaceId) return;
    setLoading(true);
    clockAPI.eodReports(workspaceId, 30)
      .then(r => setEntries((r.data || []).filter(e => e.user_id === memberId)))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  }, [workspaceId, memberId]);

  if (loading) return <div className="text-[12px] text-muted-foreground/40">Loading EOD reports…</div>;
  if (entries.length === 0) {
    return (
      <div data-testid="eod-detail-empty">
        <h3 className="text-[13px] font-semibold mb-2">EOD Reports</h3>
        <Card className="card-hover-gold">
          <CardContent className="p-4 text-[12px] text-muted-foreground/60">
            No EOD reports submitted yet — they&rsquo;ll appear here once this teammate submits one via the EOD form, or after clocking out for the day.
          </CardContent>
        </Card>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="eod-detail-section">
      <h3 className="text-[13px] font-semibold">EOD Reports ({entries.length})</h3>
      {entries.slice(0, 30).map(e => (
        <EodEntryCard key={e.id} entry={e} workspaceId={workspaceId} onTaskClick={onTaskClick} />
      ))}
    </div>
  );
}

function shortTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  } catch {
    return iso.slice(0, 16);
  }
}

function EodEntryCard({ entry, workspaceId, onTaskClick }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const loadDetail = async () => {
    if (detail) { setExpanded(!expanded); return; }
    setLoading(true);
    try {
      const r = await clockAPI.eodDetail(workspaceId, entry.id);
      setDetail(r.data);
      setExpanded(true);
    } catch {
      setDetail(null);
    }
    setLoading(false);
  };

  const exportPDF = async () => {
    // Use fetch with auth header so we don't depend on cookie auth
    const url = clockAPI.eodPdfUrl(workspaceId, entry.id);
    const token = localStorage.getItem('stellar_token') || localStorage.getItem('token');
    const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) return;
    const blob = await r.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `EOD_${(entry.user_name || 'member').replace(/\s+/g, '_')}_${entry.date}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const eod = entry.eod_report || {};
  const tasks = eod.tasks || [];
  const role = entry.user_role ? roleLabel(entry.user_role) : null;
  // D68 Bug C2 — read hours_worked first (covers standalone EOD submissions
  // where clock_in/clock_out are null and computing the diff returns 0).
  // Fall back chain: hours_worked → total_hours → eod_report.hours_worked →
  // clock_out - clock_in (only if both present) → 0.
  let displayHours = 0;
  if (entry.hours_worked != null) displayHours = Number(entry.hours_worked) || 0;
  else if (entry.total_hours != null) displayHours = Number(entry.total_hours) || 0;
  else if (eod.hours_worked != null) displayHours = Number(eod.hours_worked) || 0;
  else if (entry.clock_in && entry.clock_out) {
    try {
      const diffMs = new Date(entry.clock_out) - new Date(entry.clock_in);
      displayHours = Math.max(0, Math.round((diffMs / 3.6e6) * 10) / 10);
    } catch { displayHours = 0; }
  }

  return (
    <Card className="card-hover-gold" data-testid={`eod-entry-${entry.id}`}>
      <CardContent className="p-4 space-y-2.5">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[13px] font-semibold">{formatTaskDate(entry.date)}</span>
            {role && <Badge variant="outline" className="text-[9px]">{role}</Badge>}
            <span className="text-[11px] text-muted-foreground/50" data-testid={`eod-hours-${entry.id}`}>
              {entry.clock_in ? shortTime(entry.clock_in) : 'EOD submitted'}{entry.clock_in ? ` – ${shortTime(entry.clock_out)}` : ''} · {displayHours}h
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="h-7 text-[10px] border-border/30"
              onClick={exportPDF} data-testid={`eod-export-pdf-${entry.id}`}>
              <Download className="h-3 w-3 mr-1" /> PDF
            </Button>
            <Button variant="ghost" size="sm" className="h-7 text-[10px]"
              onClick={loadDetail} data-testid={`eod-expand-${entry.id}`}>
              {loading ? '…' : expanded ? 'Hide' : 'Detail'}
              <ChevronRight className={`h-3 w-3 ml-0.5 transition-transform ${expanded ? 'rotate-90' : ''}`} />
            </Button>
          </div>
        </div>
        {expanded && detail && (
          <div className="space-y-3 pt-2 border-t border-border/20">
            {/* Time / location / breaks */}
            <div className="text-[11px] text-muted-foreground/70 space-y-1">
              <div className="flex items-center gap-1.5">
                <MapPin className="h-3 w-3 text-muted-foreground/40" />
                <span><b>Clocked in</b> {shortTime(detail.clock_in)} {detail.location_in?.address && `at ${detail.location_in.address}`}</span>
              </div>
              {(detail.breaks || []).filter(b => b.start && b.end).length > 0 && (
                <div className="flex items-start gap-1.5">
                  <Coffee className="h-3 w-3 text-muted-foreground/40 mt-0.5" />
                  <span><b>Breaks</b> {detail.breaks_total_minutes} min ·{' '}
                    {(detail.breaks || []).filter(b => b.start && b.end).map((b, i) =>
                      `${shortTime(b.start)}–${shortTime(b.end)}`).join(' · ')}</span>
                </div>
              )}
              <div className="flex items-center gap-1.5">
                <MapPin className="h-3 w-3 text-muted-foreground/40" />
                <span><b>Clocked out</b> {shortTime(detail.clock_out)} {detail.location_out?.address && `at ${detail.location_out.address}`}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Timer className="h-3 w-3 text-muted-foreground/40" />
                <span><b>Total worked</b> {detail.total_hours || 0}h</span>
              </div>
            </div>

            {/* Per-task breakdown */}
            <div>
              <p className="text-[10px] font-semibold text-muted-foreground/50 uppercase tracking-wider mb-1">Tasks</p>
              {tasks.length === 0 ? (
                <p className="text-[11px] text-muted-foreground/40">No tracked tasks today.</p>
              ) : tasks.map((t, i) => (
                <button
                  key={t.task_id || i}
                  onClick={() => onTaskClick && t.task_id && onTaskClick(t.task_id)}
                  className="w-full text-left p-2.5 rounded-lg bg-foreground/[0.02] hover:bg-foreground/[0.05] mb-1.5 transition-colors"
                  data-testid={`eod-task-row-${t.task_id || i}`}
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-[12px] font-medium">{t.task_title}</span>
                    <span className="text-[11px] text-gold/70 tabular-nums">{t.hours}h</span>
                  </div>
                  {t.description && <p className="text-[11px] text-muted-foreground/80 mb-1">{t.description}</p>}
                  {(t.cycles || []).length > 0 && (
                    <p className="text-[10px] text-muted-foreground/50">
                      Cycles: {t.cycles.map(c => `${shortTime(c.start)}→${shortTime(c.stop)} (${c.hours}h)`).join(' · ')}
                    </p>
                  )}
                  <p className="text-[10px] text-muted-foreground/50 mt-0.5">
                    Status at end of day: {(t.status_at_eod || '').replace(/_/g, ' ')}
                  </p>
                </button>
              ))}
            </div>

            {/* Other work */}
            {eod.other_work && (
              <div>
                <p className="text-[10px] font-semibold text-muted-foreground/50 uppercase tracking-wider mb-1">Other work</p>
                <p className="text-[12px] text-muted-foreground/80 whitespace-pre-wrap p-2.5 rounded-lg bg-foreground/[0.02]">
                  {eod.other_work}
                </p>
              </div>
            )}

            {/* Footer summary */}
            <div className="flex items-center gap-3 text-[11px] pt-1 border-t border-border/20">
              <span><b className="text-gold">{eod.total_task_hours || 0}h</b> on tracked tasks</span>
              <span className="text-muted-foreground/30">·</span>
              <span><b>{detail.total_hours || 0}h</b> worked</span>
              <span className="text-muted-foreground/30">·</span>
              <span><b>{eod.gap_hours || 0}h</b> other</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────────
// 2026-02-22 — NEW EOD Reports section (Day/Week/Month + Download)
// Built per spec rebuild:
//   • Day/Week/Month toggle (defaults Day)
//   • Each EOD shown as a card: date, clock-in, clock-out, full written
//     summary, plus per-task estimated vs actual hours.
//   • Download button → CSV / PDF dropdown export for selected period.
// Uses /api/eod/team-member/{user_id}/reports + /export endpoints.
// ──────────────────────────────────────────────────────────────────────
function MemberEodReports({ workspaceId, memberId, memberName }) {
  const [period, setPeriod] = useState('day');
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorState, setErrorState] = useState(null);  // null | 'forbidden' | 'error'

  useEffect(() => {
    if (!workspaceId || !memberId) return;
    let cancelled = false;
    setLoading(true);
    setErrorState(null);
    const token = localStorage.getItem('stellar_token') || localStorage.getItem('token');
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/eod/team-member/${memberId}/reports?workspace_id=${workspaceId}&period=${period}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async r => {
        if (r.ok) return r.json();
        if (cancelled) return { reports: [] };
        if (r.status === 403) setErrorState('forbidden');
        else setErrorState('error');
        return { reports: [] };
      })
      .then(data => { if (!cancelled) setReports(data.reports || []); })
      .catch(() => { if (!cancelled) { setReports([]); setErrorState('error'); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [workspaceId, memberId, period]);

  const download = async (fmt) => {
    const token = localStorage.getItem('stellar_token') || localStorage.getItem('token');
    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/eod/team-member/${memberId}/export?workspace_id=${workspaceId}&period=${period}&format=${fmt}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) return;
    const blob = await r.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `eod-${(memberName || 'member').replace(/\s+/g, '_')}-${period}.${fmt}`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  return (
    <div className="space-y-3" data-testid="member-eod-reports-section">
      {/* 2026-02-23 — USER DIRECTIVE: AI Standup REMOVED.
          The owner wants raw chronological work-log entries only. */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <h3 className="text-[13px] font-semibold">Work Reports</h3>
        <div className="flex items-center gap-2">
          <Tabs value={period} onValueChange={setPeriod}>
            <TabsList className="h-8">
              <TabsTrigger value="day" className="text-[11px] h-6 px-2.5" data-testid="member-eod-tab-day">Day</TabsTrigger>
              <TabsTrigger value="week" className="text-[11px] h-6 px-2.5" data-testid="member-eod-tab-week">Week</TabsTrigger>
              <TabsTrigger value="month" className="text-[11px] h-6 px-2.5" data-testid="member-eod-tab-month">Month</TabsTrigger>
            </TabsList>
          </Tabs>
          <ReportDownload onDownload={download} />
        </div>
      </div>

      {loading ? (
        <div className="space-y-2">{[1, 2].map(i => <Card key={i}><CardContent className="p-4 h-[80px] animate-pulse" /></Card>)}</div>
      ) : errorState === 'forbidden' ? (
        <Card><CardContent className="p-4 text-[12px] text-rose-400" data-testid="member-eod-forbidden">You don&rsquo;t have permission to view this member&rsquo;s EOD reports.</CardContent></Card>
      ) : errorState === 'error' ? (
        <Card><CardContent className="p-4 text-[12px] text-amber-500" data-testid="member-eod-error">Couldn&rsquo;t load EOD reports. Please refresh and try again.</CardContent></Card>
      ) : reports.length === 0 ? (
        <Card><CardContent className="p-4 text-[12px] text-muted-foreground/60" data-testid="member-eod-empty">No reports submitted yet.</CardContent></Card>
      ) : (
        <div className="space-y-2.5">
          {reports.map(r => <ReportCard key={`${r.date}-${r.source}`} report={r} />)}
        </div>
      )}
    </div>
  );
}

function ReportDownload({ onDownload }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <Button variant="outline" size="sm" className="h-8 text-[11px]" onClick={() => setOpen(o => !o)} data-testid="member-eod-download-btn">
        <Download className="h-3 w-3 mr-1.5" /> Download
      </Button>
      {open && (
        <div className="absolute right-0 mt-1 z-10 bg-card border border-border/40 rounded-md shadow-lg overflow-hidden min-w-[100px]">
          <button onClick={() => { setOpen(false); onDownload('csv'); }} className="block w-full text-left px-3 py-2 text-[12px] hover:bg-foreground/[0.05]" data-testid="member-eod-download-csv">CSV</button>
          <button onClick={() => { setOpen(false); onDownload('pdf'); }} className="block w-full text-left px-3 py-2 text-[12px] hover:bg-foreground/[0.05]" data-testid="member-eod-download-pdf">PDF</button>
        </div>
      )}
    </div>
  );
}

function ReportCard({ report }) {
  const ci = report.clock_in ? new Date(report.clock_in).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '—';
  const co = report.clock_out ? new Date(report.clock_out).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '—';
  // 2026-02-23 — Total + break duration calculation per user spec.
  let totalDur = '—';
  let breakDur = '';
  try {
    if (report.clock_in && report.clock_out) {
      const inD = new Date(report.clock_in);
      const outD = new Date(report.clock_out);
      const totalMs = outD - inD;
      const breakMin = Number(report.break_minutes || report.total_break_minutes || 0);
      const workedMs = Math.max(0, totalMs - breakMin * 60_000);
      const h = Math.floor(workedMs / 3_600_000);
      const m = Math.round((workedMs % 3_600_000) / 60_000);
      totalDur = m > 0 ? `${h}h ${m}m` : `${h}h`;
      if (breakMin > 0) breakDur = ` | Break: ${Math.round(breakMin)} min`;
    }
  } catch { /* leave defaults */ }
  return (
    <Card className="card-hover-gold" data-testid={`member-eod-card-${report.date}`}>
      <CardContent className="p-4 space-y-3">
        {/* 2026-02-23 — "Work Report:" header per spec.
            Format: Work Report: [Date]
            Clock In: 9:00 AM → Clock Out: 6:00 PM | Total: 9h | Break: 30 min */}
        <div className="space-y-1">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className="text-[14px] font-semibold">
              Work Report: <span className="text-foreground/90">{formatTaskDate(report.date)}</span>
            </span>
            {report.submitted_late && (
              <Badge variant="outline" className="text-[9px] border-amber-500/40 text-amber-500" data-testid={`member-eod-late-${report.date}`}>Submitted Late</Badge>
            )}
          </div>
          <div className="text-[11.5px] text-muted-foreground/85 font-mono">
            Clock In: {ci} → Clock Out: {co} | Total: {totalDur}{breakDur}
          </div>
        </div>

        {/* Overall Notes */}
        <div>
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground/60 mb-1">Overall Notes</p>
          <p className="text-[12.5px] text-foreground/90 whitespace-pre-wrap leading-relaxed" data-testid={`member-eod-summary-${report.date}`}>
            {report.summary || <span className="text-muted-foreground/40 italic">(no overall notes)</span>}
          </p>
        </div>

        {/* Tasks Worked On — chronological work-log per task. */}
        {report.tasks && report.tasks.length > 0 && (
          <div>
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground/60 mb-1.5">Tasks Worked On</p>
            <ol className="space-y-2.5 list-none">
              {report.tasks.map((t, i) => {
                const est = Number(t.estimated_hours || 0);
                const actual = Number(t.hours || 0);
                const clientLine = t.client_name || t.client_group_name || '';
                return (
                  <li key={t.task_id || i} className="border-l-2 border-border/30 pl-3" data-testid={`member-eod-task-${report.date}-${i}`}>
                    <p className="text-[12.5px] font-medium text-foreground/95">
                      {i + 1}. {t.task_title || 'Untitled task'}
                      {clientLine && <span className="text-muted-foreground/70 font-normal"> — {clientLine}</span>}
                    </p>
                    <p className="text-[11px] text-muted-foreground/80 mt-0.5">
                      <span className="capitalize">Status: {(t.status_at_eod || 'in progress').replace(/_/g, ' ')}</span>
                      <span className="mx-1.5">|</span>
                      <span>Estimated: {est > 0 ? `${est.toFixed(1)}h` : '—'}</span>
                      <span className="mx-1.5">|</span>
                      <span>Worked: {actual > 0 ? `${actual.toFixed(2)}h` : '—'}</span>
                    </p>
                    {t.description && (
                      <p className="text-[12px] text-foreground/85 whitespace-pre-wrap mt-1 leading-relaxed">
                        Notes: {t.description}
                      </p>
                    )}
                  </li>
                );
              })}
            </ol>
          </div>
        )}
      </CardContent>
    </Card>
  );
}



// ──────────────────────────────────────────────────────────────────────
// 2026-02-22 Part 2 — Owner-only Digest Log section
//
// Renders a table of recent digest deliveries with Name | Sent | Delivered
// | Opened At | Time Range | Source. Auto-refreshes every 30s so a fresh
// open shows up without a manual reload (the tracking pixel hits the
// backend, the backend updates opened_at, and the next poll surfaces it).
// ──────────────────────────────────────────────────────────────────────
function DigestLogSection({ workspaceId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  // 2026-07-15 — "View error" + per-row retry for failed digests.
  const [errorRow, setErrorRow] = useState(null);
  const [retrying, setRetrying] = useState(false);

  const retryRow = async (row) => {
    setRetrying(true);
    try {
      const res = await digestAPI.retryDelivery(workspaceId, row.id);
      if (res.data?.ok) toast.success('Digest re-sent — accepted by the mail server');
      else toast.error(res.data?.error || 'Retry attempted but the send failed again — see the new log row.');
      setErrorRow(null);
      fetchLog(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Retry failed');
    } finally {
      setRetrying(false);
    }
  };

  const fetchLog = useCallback(async (showSpinner = false) => {
    if (!workspaceId) return;
    if (showSpinner) setRefreshing(true);
    try {
      const res = await digestAPI.getLog(workspaceId, { limit: 200 });
      setRows(res.data?.rows || []);
      setForbidden(false);
    } catch (err) {
      if (err?.response?.status === 403) setForbidden(true);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    fetchLog();
    const id = setInterval(() => fetchLog(false), 30000);
    return () => clearInterval(id);
  }, [fetchLog]);

  // 2026-02-22 — "Resend to non-openers" button. Re-sends the digest
  // ONLY to recipients who haven't opened it in the last 24h.
  const [resending, setResending] = useState(false);
  const resendNonOpeners = async () => {
    if (!workspaceId || resending) return;
    setResending(true);
    try {
      const res = await digestAPI.resendNonOpeners(workspaceId, { lookback_hours: 24 });
      const n = res.data?.resent_count ?? 0;
      const failed = res.data?.failed_count ?? 0;
      if (n === 0) toast.message('No non-openers in the last 24 hours.');
      else if (failed === 0) toast.success(`Resent to ${n} non-opener${n === 1 ? '' : 's'}.`);
      else toast.warning(`Resent to ${n}, ${failed} failed.`);
      fetchLog(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to resend');
    }
    setResending(false);
  };

  if (forbidden) {
    return <Card><CardContent className="p-6 text-[13px] text-rose-400" data-testid="digest-log-forbidden">Only the workspace owner can view the digest log.</CardContent></Card>;
  }
  if (loading) {
    return <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-12 bg-secondary/20 rounded-lg animate-pulse" />)}</div>;
  }

  const fmt = (iso) => iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';
  const sentCount = rows.filter(r => r.delivered).length;
  const failedCount = rows.filter(r => !r.delivered && r.error).length;
  const openedCount = rows.filter(r => r.opened_at).length;

  return (
    <div className="space-y-3" data-testid="digest-log-section">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-4 text-[12px] text-muted-foreground">
          <span className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> {rows.length} sent</span>
          <span className="flex items-center gap-1.5 text-green-500/80"><CheckCircle2 className="h-3.5 w-3.5" /> {sentCount} delivered</span>
          {failedCount > 0 && <span className="flex items-center gap-1.5 text-rose-400"><AlertTriangle className="h-3.5 w-3.5" /> {failedCount} failed</span>}
          <span className="flex items-center gap-1.5 text-gold"><Eye className="h-3.5 w-3.5" /> {openedCount} opened</span>
        </div>
        <Button variant="outline" size="sm" className="h-8 text-[11px]" onClick={() => fetchLog(true)} disabled={refreshing} data-testid="digest-log-refresh">
          <RefreshCw className={`h-3 w-3 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="h-8 text-[11px] border-gold/40 text-gold hover:bg-gold/10"
          onClick={resendNonOpeners}
          disabled={resending}
          data-testid="digest-log-resend-non-openers"
          title="Re-send the digest only to people who haven't opened it in the last 24 hours"
        >
          <Mail className={`h-3 w-3 mr-1.5 ${resending ? 'animate-pulse' : ''}`} /> {resending ? 'Resending…' : 'Resend to non-openers'}
        </Button>
      </div>

      {rows.length === 0 ? (
        <Card><CardContent className="p-6 text-[13px] text-muted-foreground/60" data-testid="digest-log-empty">No digests have been sent yet.</CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-hidden">
            <div className="grid grid-cols-[1.5fr_120px_110px_130px_80px_90px] gap-2 px-4 py-2.5 bg-foreground/[0.04] text-[10px] font-semibold text-muted-foreground/70 uppercase tracking-wider">
              <span>Recipient</span>
              <span>Sent</span>
              <span>Delivered</span>
              <span>Opened</span>
              <span>Range</span>
              <span>Source</span>
            </div>
            {rows.map(r => (
              <div key={r.id} className="grid grid-cols-[1.5fr_120px_110px_130px_80px_90px] gap-2 px-4 py-2.5 border-t border-border/15 items-center text-[12px]" data-testid={`digest-log-row-${r.id}`}>
                <div className="min-w-0">
                  <div className="font-medium truncate">{r.recipient_name || r.recipient_email}</div>
                  <div className="text-[10.5px] text-muted-foreground/50 truncate">{r.recipient_email}</div>
                </div>
                <span className="text-muted-foreground/80">{fmt(r.sent_at)}</span>
                <span className="flex items-center gap-1.5">
                  {r.delivered ? (
                    <Badge className="bg-green-500/15 text-green-500 border-0 text-[9.5px]" data-testid={`digest-log-delivered-${r.id}`}>Accepted</Badge>
                  ) : r.status === 'suppressed' ? (
                    <Badge className="bg-amber-500/15 text-amber-400 border-0 text-[9.5px]" title={r.error || r.reason} data-testid={`digest-log-suppressed-${r.id}`}>Suppressed</Badge>
                  ) : r.error ? (
                    <Badge className="bg-rose-500/15 text-rose-400 border-0 text-[9.5px]" title={r.error} data-testid={`digest-log-failed-${r.id}`}>Failed</Badge>
                  ) : (
                    <Badge variant="outline" className="text-[9.5px]">Pending</Badge>
                  )}
                  {!r.delivered && (r.error || r.reason) && (
                    <button
                      type="button"
                      onClick={() => setErrorRow(r)}
                      className="text-[10px] underline text-muted-foreground/70 hover:text-foreground shrink-0"
                      data-testid={`digest-log-view-error-${r.id}`}
                    >
                      view
                    </button>
                  )}
                </span>
                <span className={r.opened_at ? 'text-gold' : 'text-muted-foreground/40'} data-testid={`digest-log-opened-${r.id}`}>
                  {r.opened_at ? (
                    <span className="flex items-center gap-1"><Eye className="h-3 w-3" /> {fmt(r.opened_at)}{r.open_count > 1 && <span className="text-[10px] text-muted-foreground/50">·{r.open_count}×</span>}</span>
                  ) : '—'}
                </span>
                <span className="text-muted-foreground/70">{r.time_range_days ? `${r.time_range_days}d` : '—'}</span>
                <span className="text-[10px] text-muted-foreground/50 truncate" title={r.event_type}>
                  {r.event_type === 'team_report' ? 'Manual' : r.event_type === 'weekly_user_digest' ? 'Weekly' : r.event_type}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* 2026-07-15 — failure details + retry */}
      <Dialog open={!!errorRow} onOpenChange={(v) => { if (!v) setErrorRow(null); }}>
        <DialogContent className="max-w-md" data-testid="digest-error-dialog">
          <DialogHeader><DialogTitle>Digest delivery details</DialogTitle></DialogHeader>
          {errorRow && (
            <div className="space-y-2 text-[12px]">
              <div><span className="text-muted-foreground">Recipient:</span> {errorRow.recipient_email}</div>
              <div><span className="text-muted-foreground">Type · range:</span> {errorRow.event_type === 'team_report' ? 'Manual team report' : errorRow.event_type} · {errorRow.time_range_days ? `${errorRow.time_range_days}d` : '—'}</div>
              <div><span className="text-muted-foreground">Attempted:</span> {fmt(errorRow.last_attempt_at || errorRow.sent_at)}{errorRow.attempts ? ` · ${errorRow.attempts} attempt(s)` : ''}</div>
              <div><span className="text-muted-foreground">Transport:</span> SMTP (Hostinger){errorRow.sender_used ? ` · sender ${errorRow.sender_used}` : ''}</div>
              {errorRow.reason && <div><span className="text-muted-foreground">Category:</span> {errorRow.reason}</div>}
              <div className="p-2.5 rounded bg-rose-500/10 text-rose-300 whitespace-pre-wrap break-words text-[11.5px]" data-testid="digest-error-text">
                {errorRow.error || 'No error detail recorded (this row predates the 2026-07-15 logging upgrade). Use Retry to attempt a fresh send with full diagnostics.'}
              </div>
              <div className="flex justify-end pt-1">
                <Button size="sm" variant="outline" className="h-8 text-[11px] border-gold/40 text-gold hover:bg-gold/10" disabled={retrying} onClick={() => retryRow(errorRow)} data-testid="digest-error-retry">
                  {retrying ? 'Retrying…' : 'Retry email'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ──────────────────────────────────────────────────────────────────────
// 2026-02-23 — AI Standup Card REMOVED per user directive.
// The owner wants only raw chronological work-log entries on the member
// detail view. Function definition and its imports left out intentionally.
// ──────────────────────────────────────────────────────────────────────

